/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment-timezone';

// Component imports
import { AutoComplete, Button, Checkbox, Col, Descriptions, Divider, Form, Input, Radio, Row, Select, Skeleton, Space, Tooltip, Typography } from 'antd';
import { ArrowDownOutlined, CloseOutlined } from '@ant-design/icons';
import ActivityTypeTag from '../UI/ActivityTypeTag';
import Prompt from '../UI/Prompt';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import ReactMarkdown from 'react-markdown';
import MessageNodeAAForm from '../Graphs/Nodes/Message/MessageNodeAAForm';
import KnowledgeBaseArticleAAForm from '../Graphs/Nodes/KnowledgeBaseArticle/KnowledgeBaseArticleAAForm';
import JumpNodeAAForm from '../Graphs/Nodes/Jump/JumpNodeAAForm';
import UserFeedbackNodeAAForm from '../Graphs/Nodes/UserFeedback/UserFeedbackNodeAAForm';
import VariableNodeAAForm from '../Graphs/Nodes/Variable/VariableNodeAAForm';
import FormCard from './FormCard';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';
import { ArrowLeftIcon } from '../Icons/ArrowLeftIcon';

// Misc imports
import { DEFAULT_FALLBACK_ID, NODE_TYPES } from '../../constants/flowGraph';
import { automatedAnswerToChaptersource, chaptersourceToAutomatedAnswer, getArticleId, getKnowledgeBaseId } from '../../utils/transform/automatedAnswer';
import { useCreateAutomatedAnswerMutation, useUpdateAutomatedAnswerMutation } from '../../store/automatedAnswers/automatedAnswersApi';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { ACTIVITY_TYPES, PERMISSIONS_GROUP_EDITOR, DEFAULT_DATETIME_FORMAT } from '../../constants/main';
import { IS_USER_FEEDBACK_ENABLED } from '../../constants/env';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchKnowledgeBaseArticleQuery, useFetchKnowledgeBaseGroupQuery, useFetchKnowledgeBaseQuery } from '../../store/knowledgeBases/knowledgeBasesApi';
import { ARTICLE_TITLE_MAX_LENGTH, UNGROUPED_ARTICLES_LABEL } from '../../constants/knowledgeBases';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import extend from '../../utils/object/extend';
import clone from '../../utils/object/clone';
import useTheme from '../../utils/hooks/useTheme';
import haveDifferentElements from '../../utils/array/haveDifferentElements';
import { NAV_ORGANIZATION_KNOWLEDGE_BASE } from '../../constants/navigation';
import replaceUrlParams from '../../utils/string/replaceUrlParams';

// Constant declarations
const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const generateInitialValues = (selectedAA, primaryBotLanguage) => {
  if (selectedAA?.id) {
    return chaptersourceToAutomatedAnswer(selectedAA, primaryBotLanguage);
  }

  // Else we are creating a new AA
  return {
    content: {
      nlpTrainingData: {
        examples: {
          [primaryBotLanguage]: ['']
        }
      }
    },
    responses: [{
      type: NODE_TYPES.MESSAGE,
      options: ['']
    }]
  };

};

// TODO: Refactor this component to be more readable by breaking it down (https://helvia.atlassian.net/browse/CB-3702)
const AutomatedAnswerForm = ({
  selectedAA,
  setSelectedAA,
  hideTitle = false
}) => {
  const [form] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tenantId, orgId } = useParams();
  const { botFormPanelHeight, formProps } = useTheme();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const { isEditingPrimaryLanguage, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const urlParams = new URLSearchParams(location.search);
  // The user needs permission to edit - but also the user cannot edit if the AA has no id and is on a non-primary language (you cannot create an AA on a secondary language)
  const canEdit = userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission)) && (selectedAA?.id || isEditingPrimaryLanguage);
  const initialValues = useMemo(() => generateInitialValues(selectedAA, primaryBotLanguage), [selectedAA, primaryBotLanguage]);

  const isArticle = selectedAA.type === ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE;
  const isDefaultResponses = selectedAA.id === DEFAULT_FALLBACK_ID || urlParams.get('tab') === 'fallbacks';
  const knowledgeBaseId = getKnowledgeBaseId(selectedAA);
  const articleId = getArticleId(selectedAA);

  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });
  const { data: article, isFetching: isFetchingArticle } = useFetchKnowledgeBaseArticleQuery({ orgId, knowledgeBaseId: knowledgeBaseId, articleId: articleId }, { skip: !isArticle });
  const { data: group } = useFetchKnowledgeBaseGroupQuery({ orgId, knowledgeBaseId: knowledgeBaseId, id: article?.groupId }, { skip: !isArticle || !article || !article.groupId });
  const { data: automatedAnswers } = getCurrentQueryState('fetchAutomatedAnswers', { orgId, tenantId, currentBotLanguage });
  const [createAutomatedAnswer, { isLoading: isCreatingAutomatedAnswer }] = useCreateAutomatedAnswerMutation({ fixedCacheKey: 'create-automated-answer-main-button-shared' });
  const [updateAutomatedAnswer, { isLoading: isUpdatingAutomatedAnswer }] = useUpdateAutomatedAnswerMutation({ fixedCacheKey: 'update-automated-answer-main-button-shared' });

  // We fetch the article's KB to get its primary language
  const { data: knowledgeBase, isFetching: isFetchingKnowledgeBase } = useFetchKnowledgeBaseQuery({ orgId: orgId, knowledgeBaseId: knowledgeBaseId, urlParams: { includeTenants: true, includeIntegration: true } }, { pollingInterval: 0, /* 0 disables polling */ skip: (!knowledgeBaseId || !isArticle) });
  const [kbPrimaryLanguage, setKbPrimaryLanguage] = useState(''); // We need to keep the KB primary language to target its tags

  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [, updateState] = useState(); // Hook to force a component re-render
  const [selectedArticleContent, setSelectedArticleContent] = useState();
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (!automatedAnswers || !selectedAA) {
      return;
    }

    if (selectedAA.id) {
      setSelectedAA(automatedAnswers.items.find(automatedAnswer => automatedAnswer.id === selectedAA.id));
    }
  }, [automatedAnswers]);

  useEffect(()=>{
    // We need to keep the KB primary language to target its tags
    if (knowledgeBase && knowledgeBase?.languages.length > 0) {
      setKbPrimaryLanguage(knowledgeBase?.languages.find((language) => language.isPrimary).languageCode);
    }
  }, [knowledgeBase]);

  useEffect(() => {
    if (article) {
      const selectedArticleContent = article.content.find((content) => content.languageCode === currentBotLanguage);
      setSelectedArticleContent(selectedArticleContent);

      const primaryLanguageTags = article.content.find((content) => content.languageCode === kbPrimaryLanguage)?.tags || [];
      // compare primary language tags with locally saved tags and keep the tags into the state in order to display in any language
      if (haveDifferentElements(tags, primaryLanguageTags)) {
        setTags(primaryLanguageTags);
      }
    }
  }, [article, currentBotLanguage, tags]);

  const onSubmit = async (values) => {
    // Construct the submit values
    let valuesToTransform = clone(selectedAA);
    valuesToTransform = extend(valuesToTransform, values);

    let transformedValues;
    let isSaveSuccessful = false;

    if (!selectedAA?.id) {
      transformedValues = automatedAnswerToChaptersource(valuesToTransform, primaryBotLanguage, isDefaultResponses ? 'DEFAULT_NODE' : null);
      isSaveSuccessful = await createAutomatedAnswer({ orgId, tenantId, data: transformedValues }).unwrap();
    } else {
      // If we are in edit mode (ie. there is already an entry point ID) we don't want to change it
      transformedValues = automatedAnswerToChaptersource(valuesToTransform, primaryBotLanguage, selectedAA.content.nodes.find(node => node.type === NODE_TYPES.INTRO).id, selectedAA.type);
      isSaveSuccessful = await updateAutomatedAnswer({ orgId, tenantId, automatedAnswerId: transformedValues.id, data: transformedValues }).unwrap();
    }

    if (isSaveSuccessful) {
      setHasPendingChanges(false);
    }

    if (isSaveSuccessful && !selectedAA?.id && !isDefaultResponses) {
      form.resetFields();
      setSelectedAA(null);
    }
  };

  const renderNodeChoices = (responseIndex, responsesFields) => {
    const responseType = form.getFieldValue(['responses', responseIndex, 'type']);
    const allResponses = form.getFieldValue('responses');
    const cannotAddMoreArticles = responseType !== NODE_TYPES.KNOWLEDGE_BASE_ARTICLE && allResponses.some(field => field.type === NODE_TYPES.KNOWLEDGE_BASE_ARTICLE);

    return (
      <Form.Item
        required
        name={[responseIndex, 'type']}
        className="mb-1"
      >
        <Radio.Group onChange={() => form.setFieldValue(['responses', responseIndex, 'options', 0, currentBotLanguage], '')}>
          {
            isArticle ? (
              // If an article node already exists, disable the article option
              <Tooltip title={cannotAddMoreArticles ? t('Only one article response is allowed') : null}>
                <Radio
                  key={NODE_TYPES.KNOWLEDGE_BASE_ARTICLE}
                  value={NODE_TYPES.KNOWLEDGE_BASE_ARTICLE}
                  disabled={!canEdit || !isEditingPrimaryLanguage || cannotAddMoreArticles}
                >
                  {t(NODE_TYPES.KNOWLEDGE_BASE_ARTICLE, { context: 'automated answer response type' })}
                </Radio>
              </Tooltip>
            ) : null
          }

          <Radio
            key={NODE_TYPES.MESSAGE}
            value={NODE_TYPES.MESSAGE}
            disabled={!canEdit || !isEditingPrimaryLanguage}
          >
            {t(NODE_TYPES.MESSAGE, { context: 'automated answer response type' })}
          </Radio>

          <Tooltip title={responseIndex !== responsesFields.length - 1 ? t('Only the last node can be of type "Redirect"') : ''}>
            <Radio
              key={NODE_TYPES.JUMP}
              value={NODE_TYPES.JUMP}
              disabled={!canEdit || !isEditingPrimaryLanguage || responseIndex !== responsesFields.length - 1}
            >
              {t(NODE_TYPES.JUMP, { context: 'automated answer response type' })}
            </Radio>
          </Tooltip>

          {
            IS_USER_FEEDBACK_ENABLED ? (
              <Radio
                key={NODE_TYPES.USER_FEEDBACK}
                value={NODE_TYPES.USER_FEEDBACK}
                disabled={!canEdit || !isEditingPrimaryLanguage}
              >
                {t(NODE_TYPES.USER_FEEDBACK, { context: 'automated answer response type' })}
              </Radio>
            ) : null
          }

          <Radio
            key={NODE_TYPES.VARIABLE}
            value={NODE_TYPES.VARIABLE}
            disabled={!canEdit || !isEditingPrimaryLanguage}
          >
            {t(NODE_TYPES.VARIABLE, { context: 'automated answer response type' })}
          </Radio>
        </Radio.Group>
      </Form.Item>
    );
  };

  const renderNodeFormItems = (responseIndex) => {
    const nodeType = form.getFieldValue(['responses', responseIndex, 'type']);

    switch (nodeType) {
      case NODE_TYPES.MESSAGE:
        return (
          <MessageNodeAAForm
            form={form}
            responseIndex={responseIndex}
            canEdit={canEdit}
          />
        );

      case NODE_TYPES.KNOWLEDGE_BASE_ARTICLE:
        return (
          <KnowledgeBaseArticleAAForm
            form={form}
            responseIndex={responseIndex}
            canEdit={canEdit}
            articleId={articleId}
            knowledgeBaseId={knowledgeBaseId}
          />
        );

      case NODE_TYPES.JUMP:
        return (
          <JumpNodeAAForm
            responseIndex={responseIndex}
            canEdit={canEdit}
          />
        );

      case NODE_TYPES.USER_FEEDBACK:
        return (
          <UserFeedbackNodeAAForm
            form={form}
            responseIndex={responseIndex}
            canEdit={canEdit}
            setHasPendingChanges={setHasPendingChanges}
          />
        );

      case NODE_TYPES.VARIABLE:
        return (
          <VariableNodeAAForm
            form={form}
            responseIndex={responseIndex}
            canEdit={canEdit}
          />
        );

      default:
        return null;
    }
  };

  const calculateFormHeight = () => {
    const headerHeight = 56;
    const languageAlertHeight = 58;
    const hasLanguageAlert = document.getElementById('secondary-language-alert');

    let heightCalc = botFormPanelHeight;

    // If there is a header, subtract its height
    if (!isDefaultResponses && !hideTitle) {
      heightCalc = `calc(${heightCalc} - ${headerHeight}px)`;
    }

    // If there is a language alert, subtract the alert height
    if (hasLanguageAlert) {
      heightCalc = `calc(${heightCalc} - ${languageAlertHeight}px)`;
    }

    return heightCalc;
  };

  return (
    <Fragment>
      {
        isDefaultResponses || hideTitle ? null : (
          <Space wrap align="center" size={16} className="mb-4 w-100 justify-content-between">
            <Space size={12} align="center">
              <Button
                ghost
                size="large"
                icon={<ArrowLeftIcon />}
                className="shadow border-0"
                onClick={() => {
                  const urlSearchParams = new URLSearchParams(location.search);
                  urlSearchParams.delete('edit');
                  navigate({
                    pathname: location.pathname,
                    search: urlSearchParams.toString()
                  }, { replace: true });
                }}
              />

              <Form.Item
                noStyle
              >
                <h3 className="mb-0">
                  <Space>
                    {selectedAA?.id ? t('Edit Automated Answer') : t('New Automated Answer')}
                    <ActivityTypeTag type={selectedAA?.type || ACTIVITY_TYPES.AUTOMATED_ANSWERS} />
                  </Space>
                </h3>
              </Form.Item>
            </Space>

            {
              form.getFieldValue('createdAt') && form.getFieldValue('updatedAt') ? (
                <Col>
                  <Paragraph className="mb-0 text-muted fs-sm" strong>
                    {t('Created')}:
                    <span className="fw-normal"> {moment.tz(form.getFieldValue('createdAt'), organization?.timezone).format(DEFAULT_DATETIME_FORMAT)}</span>
                  </Paragraph>

                  <Paragraph className="mb-0 text-muted fs-sm" strong>
                    {t('Last modified')}:
                    <span className="fw-normal"> {moment.tz(form.getFieldValue('updatedAt'), organization?.timezone).format(DEFAULT_DATETIME_FORMAT)}</span>
                  </Paragraph>
                </Col>
              ) : null
            }
          </Space>
        )
      }

      <Prompt
        when={hasPendingChanges}
        onConfirm={() => form.submit()}
        okText={t('Save Changes')}
        cancelText={t('Quit without saving')}
        title={t('Save Changes?')}
        message={t('Your unsaved changes will be lost. Save changes before closing?')}
      />

      <FormCard
        layout="vertical"
        form={form}
        name={`automatedAnswerForm_${uuidv4()}`}
        cardHeight={calculateFormHeight()}
        onValuesChange={(_, allFields) => {
          setHasPendingChanges(true);
          updateState(allFields);
        }}
        onFieldsChange={(_, allFields) => {
          updateState(allFields);
        }}
        initialValues={initialValues}
        onFinish={onSubmit}
        footerActions={[
          <Button
            key="submit"
            type="primary"
            size="large"
            disabled={!hasPendingChanges}
            loading={isCreatingAutomatedAnswer || isUpdatingAutomatedAnswer}
            onClick={() => form.submit()}
          >
            {selectedAA?.id || isDefaultResponses ? t('Save Changes') : t('Create Automated Answer')}
          </Button>
        ]}
      >
        <Form.Item
          name={['settings', 'useGeneratedResponse']}
          hidden
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>

        {
          !isDefaultResponses ? (
            <Fragment>
              {
                isArticle ? (
                  <Row
                    wrap
                    gutter={formProps.rowGutter}
                  >
                    <Col xs={formProps.mainColSpan}>
                      <Paragraph className="mb-0">{t('Article Details')}</Paragraph>

                      <Paragraph className="fs-sm" type="secondary" strong style={formProps.descriptionStyle}>
                        {t('Review article details')}
                      </Paragraph>
                    </Col>

                    <Col xs={formProps.secondaryColSpan} style={{ maxWidth: formProps.maxInputWidth }}>
                      <Descriptions
                        bordered
                        size="small"
                        column={1}
                        labelStyle={{ minWidth: 100 }}
                      >
                        <Descriptions.Item label={<Text type="secondary">{t('Title')}</Text>}>
                          {selectedArticleContent?.title || form.getFieldValue('displayName')}
                        </Descriptions.Item>

                        <Descriptions.Item label={<Text type="secondary">{t('Group')}</Text>}>
                          {group?.name[currentBotLanguage] || group?.name[primaryBotLanguage] || UNGROUPED_ARTICLES_LABEL}
                        </Descriptions.Item>

                        <Descriptions.Item label={<Text type="secondary">{t('Knowledge Base')}</Text>}>
                          {
                            isFetchingKnowledgeBase
                              ? (
                                <Skeleton.Input style={{ width: 100 }} active size="small" />
                              )
                              : <Link to={`${replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASE, { orgId: orgId, knowledgeBaseId: knowledgeBase.id })}?article=${articleId}`}>{knowledgeBase?.name}</Link>

                          }
                        </Descriptions.Item>

                        <Descriptions.Item label={<Text type="secondary">{t('Tags')}</Text>}>
                          {
                            isFetchingArticle ? (
                              <Skeleton.Input style={{ width: 100 }} active size="small" />
                            ) : tags?.length > 0 ? (
                              <ResponsiveTagsList
                                values={tags.map((tag, index) => {
                                  return { label: tag, value: index };
                                })}
                                mode="tags"
                                className="w-100"
                                tagRender={(tag) => <span className="ant-select-selection-item p-0 px-1 mr-2">{tag.label}</span>}
                                disabled
                              />
                            ) : '-'
                          }
                        </Descriptions.Item>

                        <Descriptions.Item label={<Text type="secondary">{t('Article Body')}</Text>}>
                          {
                            !selectedArticleContent?.body ? (
                              <Text>
                                {t('(No article body)')}
                              </Text>
                            ) : null
                          }
                        </Descriptions.Item>

                      </Descriptions>

                      {
                        selectedArticleContent?.body ? (
                          <div
                            className="bg-layout w-100 kb-article-body word-break-break-word p-4 border-radius-base scrollbar-custom scrollbar-custom-nohide overflow-y-auto"
                            style={{ maxHeight: 300 }}
                          >
                            <ReactMarkdown skipHtml>
                              {selectedArticleContent?.body}
                            </ReactMarkdown>
                          </div>
                        ) : null
                      }

                      <Form.Item
                        name="activityId"
                        hidden
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : (
                  <Row
                    wrap
                    gutter={formProps.rowGutter}
                  >
                    <Col xs={formProps.mainColSpan}>
                      <Paragraph className="mb-0">{t('Automated Answer Details')}</Paragraph>

                      <Paragraph className="fs-sm" type="secondary" strong style={formProps.descriptionStyle}>
                        {t('Set the name and group for this automated answer')}
                      </Paragraph>
                    </Col>

                    <Col xs={formProps.secondaryColSpan} style={{ maxWidth: formProps.maxInputWidth }}>
                      <Form.Item
                        name={['name', currentBotLanguage]}
                        label={t('Name')}
                        rules={[{
                          required: true,
                          message: t('Please enter a name for this Automated Answer')
                        }, {
                          validator: async (rule, value) => {
                            try {
                              if (value && value.length > ARTICLE_TITLE_MAX_LENGTH) {
                                throw new Error(rule.message);
                              }
                            } catch {
                              throw new Error(rule.message);
                            }
                          },
                          message: t('Exceeded character limit')
                        }]}
                      >
                        <Input
                          placeholder={t('Enter Automated Answer name')}
                          size="large"
                          disabled={!canEdit}
                        />
                      </Form.Item>

                      <Form.Item
                        name="activityId"
                        hidden
                      >
                        <Input size="large" />
                      </Form.Item>

                      <Form.Item
                        name={['group', currentBotLanguage]}
                        label={t('Group')}
                        rules={[{
                          required: true,
                          message: t('Please select a group or add a new one')
                        }]}
                      >
                        <AutoComplete
                          options={
                            [...new Set((automatedAnswers?.items || [])
                              .map(a => a.group[currentBotLanguage]))]
                              .map(groupName => ({ value: groupName }))
                          }
                          disabled={!canEdit}
                          className="autocomplete-noicon"
                          getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
                        >
                          <Input
                            size="large"
                            placeholder={t('Add or choose a group')}
                          />
                        </AutoComplete>
                      </Form.Item>

                      {/* Tags Field */}
                      <Form.Item
                        name="tags"
                        label={<Text type="secondary">{t('Tags')}</Text>}
                        labelAlign="left"

                      >
                        <Select
                          mode="tags"
                          size="large"
                          tokenSeparators={[',']}
                          placeholder={t('Add tags for the article')}
                          disabled={!isEditingPrimaryLanguage}
                          options={
                            initialValues?.tags?.length > 0 ? initialValues?.tags.map((tag)=> {
                              return {
                                value: tag,
                                label: tag
                              };
                            }) : null
                          }
                          getPopupContainer={triggerNode => triggerNode.parentElement}
                          maxTagPlaceholder={(omittedValues) => (
                            <Tooltip
                              title={
                                <div
                                  className="overflow-y-auto overflow-x-hidden scrollbar-custom"
                                  style={{ maxHeight: 280, maxWidth: 220 }}
                                >
                                  {
                                    omittedValues.map((value, index) => (
                                      <Text.Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                                        {value.label}

                                        <Button
                                          size="small"
                                          type="link"
                                          className="mb-1"
                                          icon={<CloseOutlined className="text-secondary mr-0 fs-sm"/>}
                                        />
                                      </Text.Paragraph>
                                    ))
                                  }
                                </div>
                              }
                              placement="bottomLeft"
                            >
                          +{omittedValues.length}
                            </Tooltip>
                          )}
                        />
                      </Form.Item>
                      {/* ./ Add tags */}
                    </Col>
                  </Row>
                )
              }

              <Divider />

              {
                !isArticle ? (
                  <Fragment>
                    <Row
                      wrap
                      gutter={formProps.rowGutter}
                    >
                      <Col xs={formProps.mainColSpan}>
                        <Paragraph className="mb-0">{t('Agent Input')}</Paragraph>

                        <Paragraph className="fs-sm" type="secondary" strong style={formProps.descriptionStyle}>
                          {t('Provide the text that will be used to train the agent\'s AI')}
                        </Paragraph>
                      </Col>

                      <Col xs={formProps.secondaryColSpan} style={{ maxWidth: formProps.maxInputWidth }}>
                        <Form.List
                          name={['nlpTrainingData', 'examples', currentBotLanguage]}
                        >
                          {(fields, { add, remove }, { errors }) => (
                            <Fragment>
                              {fields.map((field, _index) => (
                                <Form.Item
                                  required={false}
                                  key={field.key}
                                >
                                  <Row gutter={[8, 8]} align="middle" wrap={false}>
                                    <Col flex="auto">
                                      <Form.Item
                                        {...field}
                                        validateTrigger="onChange"
                                        rules={[
                                          {
                                            required: true,
                                            message: t('Please enter an input or delete this field')
                                          }
                                        ]}
                                        noStyle
                                      >
                                        <TextArea
                                          placeholder={t('Enter agent input')}
                                          size="large"
                                          onPressEnter={(event) => {
                                            event.preventDefault();
                                            add();
                                          }}
                                          style={{ maxHeight: 360 }}
                                          disabled={!canEdit}
                                          autoFocus={!form.getFieldValue(['nlpTrainingData', 'examples', currentBotLanguage])[_index]?.length > 0}
                                        />
                                      </Form.Item>
                                    </Col>

                                    {
                                      fields.length > 0 ? (
                                        <Col flex="none">
                                          <Button
                                            type="text"
                                            shape="circle"
                                            className="text-primary"
                                            icon={<DeleteIcon />}
                                            onClick={() => remove(field.name)}
                                            disabled={!canEdit}
                                          />
                                        </Col>
                                      ) : null
                                    }
                                  </Row>
                                </Form.Item>
                              ))}

                              <Form.ErrorList errors={errors} />

                              <Button
                                type="dashed"
                                size="large"
                                block
                                onClick={() => add()}
                                className="text-muted"
                                icon={<PlusIcon />}
                                disabled={!canEdit}
                              >
                                {t('Add Agent Input')}
                              </Button>
                            </Fragment>
                          )}
                        </Form.List>
                      </Col>
                    </Row>

                    <Divider />
                  </Fragment>
                ) : null
              }
            </Fragment>
          ) : (
            <Fragment>
              <Form.Item
                name="id"
                hidden
                initialValue={DEFAULT_FALLBACK_ID}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name={['name', primaryBotLanguage]}
                hidden
                initialValue={DEFAULT_FALLBACK_ID}
              >
                <Input />
              </Form.Item>
            </Fragment>
          )
        }

        <Row
          wrap
          gutter={[16, 16]}
        >
          <Col xs={formProps.mainColSpan}>
            <Paragraph className="mb-0">{isDefaultResponses ? t('Default Flow') : t('Agent Responses')}</Paragraph>
            <Paragraph className="fs-sm" type="secondary" strong style={formProps.descriptionStyle}>
              {
                isDefaultResponses
                  ? null
                  : isArticle
                    ? t('Configure the Agent responses')
                    : t('Configure the Agent responses for the above inputs')
              }
            </Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan} style={{ maxWidth: formProps.maxInputWidth }}>
            <Form.List
              name="responses"
            >
              {(responsesFields, { add: addResponse, remove: removeResponse }, { errors: responsesErrors }) => (
                <Fragment>
                  <Button
                    type="dashed"
                    size="large"
                    block
                    onClick={() => {
                      addResponse({ type: NODE_TYPES.MESSAGE, options: [''] }, 0);
                    }}
                    className="text-secondary mb-3"
                    icon={<PlusIcon />}
                    disabled={!canEdit || !isEditingPrimaryLanguage}
                  >
                    {t('Add Agent Response')}
                  </Button>

                  {responsesFields.map(({ key: responseKey, name: responseIndex }) => {
                    return (
                      <Fragment key={responseKey}>
                        <Row wrap={false} align="middle">
                          <Col flex="auto">
                            <div className="bg-layout p-3 border-radius-base">
                              {renderNodeChoices(responseIndex, responsesFields)}
                              {renderNodeFormItems(responseIndex)}
                            </div>

                            {
                              responseIndex < responsesFields.length - 1 ? (
                                <Row justify="center" className="my-2">
                                  <ArrowDownOutlined className="fs-lg text-secondary"/>
                                </Row>
                              ) : null
                            }
                          </Col>

                          {
                            responsesFields.length > 1 ? (
                              <Col flex="none">
                                <Button
                                  type="text"
                                  shape="circle"
                                  className="text-primary"
                                  icon={<DeleteIcon />}
                                  onClick={() => removeResponse(responseIndex)}
                                  disabled={!canEdit || !isEditingPrimaryLanguage}
                                />
                              </Col>
                            ) : null
                          }
                        </Row>
                      </Fragment>
                    );
                  })}

                  <Form.ErrorList errors={responsesErrors} />

                  {
                    !(form.getFieldValue(['responses', responsesFields.length - 1, 'type']) === NODE_TYPES.JUMP) ? (
                      <Button
                        type="dashed"
                        size="large"
                        block
                        onClick={() => {
                          addResponse({ type: NODE_TYPES.MESSAGE, options: [''] });
                        }}
                        className="text-secondary mt-3"
                        icon={<PlusIcon />}
                        disabled={!canEdit || !isEditingPrimaryLanguage}
                      >
                        {t('Add Agent Response')}
                      </Button>
                    ) : null
                  }
                </Fragment>
              )}
            </Form.List>
          </Col>
        </Row>
      </FormCard>
    </Fragment>
  );
};

AutomatedAnswerForm.propTypes = {
  selectedAA: PropTypes.object,
  setSelectedAA: PropTypes.func,
  isDefaultResponses: PropTypes.bool,
  hideTitle: PropTypes.bool
};

export default AutomatedAnswerForm;
